import React, { useState, useEffect, useRef } from 'react';
import { apiClient } from '../../services/api';
import { EmailGroupSummary, EmailGroup } from '../../types/survey';
import { useLanguage } from '../../contexts/LanguageContext';

const EmailGroupManagement: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t } = useLanguage();
  const [groups, setGroups] = useState<EmailGroupSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState<EmailGroup | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDesc, setNewGroupDesc] = useState('');
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberName, setNewMemberName] = useState('');
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ email: string; success: boolean; message: string } | null>(null);

  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    try {
      setLoading(true);
      const data = await apiClient.getEmailGroups();
      setGroups(data);
    } catch (err) {
      console.error('Failed to load groups:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      alert(t('emailGroup', 'nameRequired'));
      return;
    }
    try {
      setSaving(true);
      const group = await apiClient.createEmailGroup({
        name: newGroupName.trim(),
        description: newGroupDesc.trim(),
        members: []
      });
      setShowCreateForm(false);
      setNewGroupName('');
      setNewGroupDesc('');
      await loadGroups();
      setSelectedGroup(group);
    } catch (err) {
      alert(t('emailGroup', 'createFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGroup = async (groupId: string) => {
    if (!window.confirm(t('emailGroup', 'deleteConfirm'))) return;
    try {
      await apiClient.deleteEmailGroup(groupId);
      if (selectedGroup?.groupId === groupId) setSelectedGroup(null);
      await loadGroups();
    } catch (err) {
      alert(t('common', 'deleteFailed'));
    }
  };

  const selectGroup = async (groupId: string) => {
    try {
      const group = await apiClient.getEmailGroup(groupId);
      setSelectedGroup(group);
    } catch (err) {
      console.error('Failed to load group:', err);
    }
  };

  const addMember = async () => {
    if (!selectedGroup || !newMemberEmail.trim()) return;
    const updatedMembers = [...selectedGroup.members, { email: newMemberEmail.trim(), name: newMemberName.trim() || newMemberEmail.trim() }];
    try {
      const updated = await apiClient.updateEmailGroup(selectedGroup.groupId, { members: updatedMembers });
      setSelectedGroup(updated);
      setNewMemberEmail('');
      setNewMemberName('');
      await loadGroups();
    } catch (err) {
      alert(t('emailGroup', 'addMemberFailed'));
    }
  };

  const removeMember = async (email: string) => {
    if (!selectedGroup) return;
    const updatedMembers = selectedGroup.members.filter(m => m.email !== email);
    try {
      const updated = await apiClient.updateEmailGroup(selectedGroup.groupId, { members: updatedMembers });
      setSelectedGroup(updated);
      await loadGroups();
    } catch (err) {
      alert(t('emailGroup', 'removeMemberFailed'));
    }
  };

  const testEmail = async (email: string) => {
    try {
      setTestingEmail(email);
      setTestResult(null);
      const result = await apiClient.testSes(email);
      setTestResult({ email, success: result.success, message: result.message });
    } catch (err: any) {
      setTestResult({
        email,
        success: false,
        message: err.response?.data?.error?.message || err.message || t('emailGroup', 'testFailed')
      });
    } finally {
      setTestingEmail(null);
    }
  };

  const handleCsvImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedGroup) return;
    try {
      const updated = await apiClient.importEmailGroupCsv(selectedGroup.groupId, file);
      setSelectedGroup(updated);
      await loadGroups();
    } catch (err) {
      alert(t('emailGroup', 'csvImportFailed'));
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  if (loading) return <div style={{ padding: '30px' }}>{t('common', 'loading')}</div>;

  return (
    <div style={{ padding: '30px', maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0 }}>{t('emailGroup', 'management')}</h2>
        <button
          onClick={() => setShowCreateForm(true)}
          style={{ padding: '8px 16px', backgroundColor: '#2c5282', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem' }}
        >
          {t('emailGroup', 'newGroup')}
        </button>
      </div>

      {/* Create form */}
      {showCreateForm && (
        <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', marginBottom: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: '1rem' }}>{t('emailGroup', 'createGroup')}</h3>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
            <input
              type="text"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              placeholder={t('emailGroup', 'groupName')}
              style={{ flex: 1, padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '0.9rem' }}
            />
            <input
              type="text"
              value={newGroupDesc}
              onChange={(e) => setNewGroupDesc(e.target.value)}
              placeholder={t('emailGroup', 'descriptionPlaceholder')}
              style={{ flex: 1, padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '0.9rem' }}
            />
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={handleCreateGroup}
              disabled={saving}
              style={{ padding: '6px 14px', backgroundColor: '#2c5282', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}
            >
              {saving ? t('common', 'creating') : t('common', 'create')}
            </button>
            <button
              onClick={() => { setShowCreateForm(false); setNewGroupName(''); setNewGroupDesc(''); }}
              style={{ padding: '6px 14px', backgroundColor: '#edf2f7', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem', color: '#4a5568' }}
            >
              {t('common', 'cancel')}
            </button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '20px' }}>
        {/* Group list */}
        <div style={{ flex: '0 0 300px' }}>
          {groups.length === 0 ? (
            <p style={{ color: '#718096', fontSize: '0.9rem' }}>{t('emailGroup', 'noGroups')}</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {groups.map(g => (
                <div
                  key={g.groupId}
                  onClick={() => selectGroup(g.groupId)}
                  style={{
                    padding: '12px 16px', borderRadius: '6px', cursor: 'pointer',
                    backgroundColor: selectedGroup?.groupId === g.groupId ? '#ebf8ff' : 'white',
                    border: `1px solid ${selectedGroup?.groupId === g.groupId ? '#90cdf4' : '#e2e8f0'}`,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: '0.9rem' }}>{g.name}</div>
                      <div style={{ color: '#718096', fontSize: '0.8rem' }}>{g.memberCount}{t('common', 'people')}</div>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteGroup(g.groupId); }}
                      style={{ padding: '4px 8px', backgroundColor: '#fff5f5', color: '#e53e3e', border: '1px solid #fed7d7', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' }}
                    >
                      {t('common', 'delete')}
                    </button>
                  </div>
                  {g.description && <div style={{ color: '#a0aec0', fontSize: '0.75rem', marginTop: '4px' }}>{g.description}</div>}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Group detail */}
        {selectedGroup && (
          <div style={{ flex: 1, backgroundColor: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '1.1rem' }}>{selectedGroup.name}</h3>

            {/* Add member */}
            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f7fafc', borderRadius: '6px' }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 500, marginBottom: '8px' }}>{t('emailGroup', 'addMember')}</div>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                <input
                  type="email"
                  value={newMemberEmail}
                  onChange={(e) => setNewMemberEmail(e.target.value)}
                  placeholder={t('common', 'email')}
                  style={{ flex: 1, padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '0.85rem' }}
                  onKeyDown={(e) => { if (e.key === 'Enter') addMember(); }}
                />
                <input
                  type="text"
                  value={newMemberName}
                  onChange={(e) => setNewMemberName(e.target.value)}
                  placeholder={t('common', 'name')}
                  style={{ flex: 1, padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '0.85rem' }}
                  onKeyDown={(e) => { if (e.key === 'Enter') addMember(); }}
                />
                <button
                  onClick={addMember}
                  style={{ padding: '6px 12px', backgroundColor: '#2c5282', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}
                >
                  {t('common', 'add')}
                </button>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.txt"
                  onChange={handleCsvImport}
                  style={{ fontSize: '0.8rem' }}
                />
                <span style={{ color: '#a0aec0', fontSize: '0.75rem' }}>{t('emailGroup', 'csvBulkAdd')}</span>
              </div>
            </div>

            {/* Member list */}
            <div style={{ fontSize: '0.85rem', color: '#718096', marginBottom: '8px' }}>
              {t('emailGroup', 'members')} ({selectedGroup.members.length}{t('common', 'people')})
            </div>
            {selectedGroup.members.length === 0 ? (
              <p style={{ color: '#a0aec0', fontSize: '0.85rem' }}>{t('emailGroup', 'noMembers')}</p>
            ) : (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {selectedGroup.members.map((m, i) => (
                  <div key={i} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '8px 10px', borderBottom: '1px solid #f0f0f0'
                  }}>
                    <div>
                      <span style={{ fontSize: '0.85rem' }}>{m.name}</span>
                      <span style={{ marginLeft: '8px', color: '#718096', fontSize: '0.8rem' }}>{m.email}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {testResult && testResult.email === m.email && (
                        <span style={{
                          fontSize: '0.75rem',
                          color: testResult.success ? '#38a169' : '#e53e3e',
                          maxWidth: '150px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {testResult.success ? '✓' : '✗'}
                        </span>
                      )}
                      <button
                        onClick={() => testEmail(m.email)}
                        disabled={testingEmail === m.email}
                        style={{
                          padding: '2px 8px',
                          backgroundColor: testingEmail === m.email ? '#e2e8f0' : '#ebf8ff',
                          color: testingEmail === m.email ? '#a0aec0' : '#2c5282',
                          border: '1px solid #bee3f8',
                          borderRadius: '4px',
                          cursor: testingEmail === m.email ? 'not-allowed' : 'pointer',
                          fontSize: '0.75rem'
                        }}
                      >
                        {testingEmail === m.email ? t('emailGroup', 'testing') : t('emailGroup', 'testEmail')}
                      </button>
                      <button
                        onClick={() => removeMember(m.email)}
                        style={{ padding: '2px 8px', backgroundColor: 'transparent', color: '#e53e3e', border: 'none', cursor: 'pointer', fontSize: '0.8rem' }}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailGroupManagement;
